<template>
  <div>
    <div class="d-flex justify-content-end mb-1">
      <div class="col-4">
        <b-form-input
          v-model="searchQuery"
          placeholder="Buscar por SOEID, nombre"
        />
      </div>
      <div class="col-auto px-0">
        <b-button
          variant="primary"
          class="w-100"
          :disabled="loading"
          @click="$emit('add-shelter')"
        >
          <feather-icon icon="PlusIcon" class="mr-25" />
          <span class="text-nowrap">Agregar</span>
        </b-button>
      </div>
    </div>
    <b-table
      ref="refListTable"
      class="position-relative mt-2"
      :items="fetchList"
      responsive
      :fields="tableColumns"
      primary-key="id"
      :sort-by.sync="sortBy"
      show-empty
      :empty-text="$t('no_record_found')"
      :sort-desc.sync="isSortDirDesc"
    >

      <template #head()="data">
        <span>{{ data.label }}</span>
      </template>

      <template #cell(actions)="data">
        <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item  @click="$emit('edit-shelter', data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50 text-capitalize">{{$t("actions.edit")}}</span>
          </b-dropdown-item>
          <b-list-group-item
            class="border-0"
            button
            @click="confirmDelete(data.item)"
          >
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50 text-capitalize">
              {{ $t("actions.delete") }}
            </span>
          </b-list-group-item>
        </b-dropdown>
      </template>
    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted text-capitalize">{{
            $t("table.showing", {
              from: dataMeta.from,
              to: dataMeta.to,
              of: dataMeta.of,
            })
          }}</span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <Pagination
            data-cy="pagination"
            :totalList="totalList"
            :currentPage="currentPage"
            @updatePagination="update"
            :perPage="perPage"
          >
          </Pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios';
import Swal from "sweetalert2";
import { useToast } from "vue-toastification/composition";
import { computed,ref,watch } from '@vue/composition-api';
import Pagination from "@/components/Pagination.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  props: {
    crisis: {
      type: Object
    },
    municipalityData: {
      type: Object
    }
  },
  components: {
    Pagination,
    ToastificationContent
  },
  setup(props, { emit }) {
    //COMPOSABLES
    const toast = useToast();

    //DATA
    const currentPage = ref(1);

    const isSortDirDesc = ref(true);

    const loading = ref(false);

    const perPage = ref(7);

    const perPageOptions = [10, 25, 50, 100];

    const refListTable = ref(null);

    const searchQuery = ref('');

    const sortBy = ref('id');

    const tableColumns = [
      { key: 'name', label: 'Nombre'},
      { key: 'address', label: 'Dirección' },
      { key: 'phone_1', label: 'Teléfono 1', },
      { key: 'phone_2', label: 'Teléfono 2', },
      { key: 'actions', label: 'Acciones'}
    ];

    const totalList = ref(0);

    //COMPUTED
    const dataMeta = computed(() => {
      const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
      return {
        from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalList.value,
      }
    });

    const localIsActive = computed({
      get() {
        return props.showModal;
      },
      set(value) {
        emit("update:isActive", value);
      },
    });

    //METHODS
    const confirmDelete = (shelterDelete) => {
      Swal.fire({
        title: `¿Eliminar albergue ${shelterDelete.name}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          axios.delete(`v1/operational-security-crisis/municipality/${shelterDelete.id_municipality}/shelters/${shelterDelete.id}/delete`)
            .then((response) => {
              const { data } = response;

              toast({
                component: ToastificationContent,
                props: {
                  title: data.message,
                  icon: "CheckIcon",
                  variant: "success",
                },
              });

              refetchData();
              emit('refresh-shelters');
            });
        }
      });
    }

    const fetchList = (ctx, callback) => {
      const params = {
        q: searchQuery.value,
        max: perPage.value ,
        'page[n]': currentPage.value,
        id_municipality: props.municipalityData.id,
        id_operational_security_crisis: props.crisis.id
      }

      axios.get('v1/shelters', { params })
      .then(response => {
        callback(response.data.data)
        totalList.value = response.data.meta.total
      })
      .catch((error) => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
    }

    const onHide = () => {
      searchQuery.value = '';
      currentPage.value = 1;

      emit('update:isActive', false);
    }

    const refetchData = () => {
      refListTable.value.refresh()
    }

    const update = (data) => {
      currentPage.value = data;
    }

    //WATCHERS
    watch([currentPage,perPage,searchQuery], () => {
      refetchData();
    });

    return {
      currentPage,
      isSortDirDesc,
      loading,
      perPage,
      perPageOptions,
      refListTable,
      searchQuery,
      sortBy,
      tableColumns,
      totalList,

      dataMeta,
      localIsActive,

      confirmDelete,
      fetchList,
      onHide,
      update
    }
  }
}
</script>

<style lang="scss" scoped>
.filters-row {
  gap: 10px;
}

.filter-field {
  min-width: 230px;
  max-width: 100%;
}
</style>
